import { useMemo, useState } from 'react';

import useSWRInfinite from 'swr/infinite';

//types
import type { ErrorCustom } from '@/utils/client';
// Utils
import { get } from '@/utils/client';

import type {
    LibraryMediaDataType,
    UseLibraryMediaProps
} from '@/utils/hooks/swr/useLibraryMedia/types/types';

// API Endpoints
import { LIBRARY_MEDIA } from '@/lib/routes/apiEndpoints';

const getKey = (
    pageIndex: number,
    previousPageData: LibraryMediaDataType,
    activeSPID: string,
    limit: number,
    requestParams: UseLibraryMediaProps['params']
) => {
    // Reached the end
    if ((previousPageData && !previousPageData.data) || !activeSPID) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0)
        return [
            `${LIBRARY_MEDIA.replace('%SPID%', activeSPID)}?per_page=${limit}`,
            JSON.stringify(requestParams)
        ];

    // Add the cursor to the API endpoint
    return [
        `${LIBRARY_MEDIA.replace('%SPID%', activeSPID)}?per_page=${limit}&cursor=${
            previousPageData.cursor
        }`,
        JSON.stringify(requestParams)
    ];
};

const useLibraryMedia = ({
    activeSPID,
    limit = 20,
    params = {},
    options = {}
}: UseLibraryMediaProps) => {
    const [requestParams, setRequestParams] = useState(params);

    const {
        data: dataLibraryMedia,
        size,
        setSize,
        mutate: mutateLibraryMedia,
        error: libraryMediaError
    } = useSWRInfinite<LibraryMediaDataType, ErrorCustom>(
        (pageIndex, previousPageData) =>
            getKey(pageIndex, previousPageData, activeSPID, limit, requestParams),
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const medias = useMemo(
        () => dataLibraryMedia?.flatMap((page) => page.data) ?? [],
        [dataLibraryMedia]
    );

    const isLoadingInitialData = !dataLibraryMedia && !libraryMediaError;
    const isLoadingMore =
        size > 0 && dataLibraryMedia && typeof dataLibraryMedia[size - 1] === 'undefined';

    return {
        dataLibraryMedia,
        medias,
        loadMoreLibraryMedia: () => setSize(size + 1), // Function to load more media (load more button)
        mutateLibraryMedia,
        libraryMediaError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataLibraryMedia &&
            typeof dataLibraryMedia[dataLibraryMedia.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useLibraryMedia;
